import { AxiosResponse } from "axios";
import { Options, Vue } from "vue-class-component";
import { provide } from "vue";
import { configuration, service } from "@/config/host.config";
import { minute as minutes } from "@/services/date-time/time-controll";
import { IConfigurationApp } from "@/@types/configuration-app/mobile-configuration";
import { ISettings } from "@/@types/configuration-app/mobile-configuration";
import { configurations } from "@/store/modules/hotel/configuration";
import { useCurrencyStore } from "@/store/modules/_shared/currency";
import { HotelSettingsBase } from "@/@types/hotel-services/settings";
import { useAuthorizationStore } from "@/store/modules/authorization";
import { HotelService } from "@/@types/servises/servises";
import { dateTimeDecoder } from "@/services/date-time/date-converter";
import { useInformers } from "@/store/modules/hotel/informers";
import { isServerReachable } from "@/services/connectivity";
import type { RoutingPage } from "@/@types/shared/nav/nav-bar";

import i18n from "@/i18n";
import type { Locale, LanguageCode } from "@/@types/shared/locale";
@Options({
  name: "configuration",
  watch: {
    isAuthorized(this: TvSettings, newValue) {
      if (newValue && !this.confStarted) {
        this.confStarted = true;
        this.startconf();
      }
    },
  },
})
export default class TvSettings extends Vue {
  configurationInterval!: ReturnType<typeof setInterval>;
  informersInterval!: ReturnType<typeof setInterval>;
  backgroundFolder = provide("backgroundFolder", "main/configuration/background/mobile");

  configurations = configurations();
  useCurrencyStore = useCurrencyStore();

  get isAuthorized(): boolean {
    return useAuthorizationStore().isAuthorized;
  }

  private confStarted = false;

  get orientation() {
    return configurations().orientation;
  }

  mounted(): void {
    this.startconf();
    this.startInformers();
  }

  beforeUnmount(): void {
    clearInterval(this.configurationInterval);
    clearInterval(this.informersInterval);
  }

  startconf(): void {
    this.configurationInterval = setInterval(() => {
      if (!this.isAuthorized) return;
      this.createConfiguration();
      this.createHotelConfig();
      this.createRouteConfiguration();
    }, minutes(10));

    this.createConfiguration();
    this.createHotelConfig();
    this.createRouteConfiguration();
    this.crealeCurrencyList();
  }

  startInformers(): void {
    this.informersInterval = setInterval(() => {
      if (!this.isAuthorized) return;
      this.createInformelList();
    }, this.$second(10));
    this.createInformelList();
  }

  createConfigHotel(set: HotelSettingsBase): void {
    if (set) this.configurations.setHotel(set);
  }

  createConfiSettings(config: ISettings): void {
    if (config.languages && config.languages.length)
      this.configurations.setLangs(config.languages);
    if (config.weather) this.configurations.setWeather(config.weather);
    if (config.internet_sources)
      this.configurations.setInternetSources(config.internet_sources);
    if (config.currencys) this.configurations.setCurensys(config.currencys);
  }

  createConfigApp(config: IConfigurationApp): void {
    this.configurations.setBackground(config.background);
    this.configurations.setLogo(config.logo);
  }

  mapServicesToInformers(services: HotelService[]): void {
    const useInformerStore = useInformers();
    useInformerStore.cleanInformers();
    services.forEach((service) => {
      switch (service.serviceType) {
        case "WAKE_UP":
          useInformerStore.setInformer({
            _id: "WAKE_UP",
            name: this.createTransliteWakeUp(service.details.wakeUpTime),
            src: { icon: { icon: "bi-clock", lib: "bi" } },
          });
          break;
        case "CONCIERGE_CALL":
          useInformerStore.setInformer({
            _id: "CONCIERGE_CALL",
            name: this.createTransliteConciergeCall(),
            src: {},
          });
          break;
        case "HYGIENE_SUPPLIES":
          useInformerStore.setInformer({
            _id: "HYGIENE_SUPPLIES",
            name: { ru: "Гигиенические принадлежности", en: "Hygiene Supplies" },
            src: { icon: { icon: "bi-clock", lib: "bi" } },
          });
          break;
        case "CLEANING_REQUEST":
          useInformerStore.setInformer({
            _id: "CLEANING_REQUEST",
            name: { ru: "Уборка", en: "Cleaning" },
            src: { icon: { icon: "bi-clock", lib: "bi" } },
          });
          break;
        case "DO_NOT_DISTURB":
          useInformerStore.setInformer({
            _id: "DO_NOT_DISTURB",
            name: { ru: "Не беспокоить", en: "Do Not Disturb" },
            src: { icon: { icon: "bi-clock", lib: "bi" } },
          });
          break;
        case "EMERGENCY_ASSISTANCE":
          useInformerStore.setInformer({
            _id: "EMERGENCY_ASSISTANCE",
            name: { ru: "Экстренная помощь", en: "Emergency Assistance" },
            src: { icon: { icon: "bi-clock", lib: "bi" } },
          });
          break;
      }
    });
  }
  createTransliteWakeUp(wakeUpTime: Date): Locale {
    const timer = dateTimeDecoder(wakeUpTime);
    const locales: LanguageCode[] = configurations().getLags;

    if (!timer) return {};

    const hour = timer.hour;
    const minute = timer.minute;

    console.log(hour + " " + minute);

    const translite: Partial<Locale> = {};
    locales.forEach((lang) => {
      const text = `${i18n.global.t("date.hours._short", {
        count: hour,
        locale: lang,
      })} ${i18n.global.t("date.minutes._short", {
        count: minute,
        locale: lang,
      })}`;

      translite[lang] = text;
    });

    return translite as Locale;
  }
  createTransliteConciergeCall(): Locale {
    const locales: LanguageCode[] = configurations().getLags;
    const translite: Partial<Locale> = {};
    locales.forEach((lang) => {
      const text = i18n.global.t("staff.concierge-call");
      translite[lang] = text;
    });

    return translite as Locale;
  }

  async getRoutes(): Promise<RoutingPage[]> {
    const uri = configuration.pages.main;
    const params = { "match[show]": true };
    const res: AxiosResponse<RoutingPage[]> = await this.$axios.get(uri, { params });
    if (res.status === 200) return res.data;
    throw new Error(`bad status: ${res.status}`);
  }

  async createRouteConfiguration(): Promise<void> {
    if (!isServerReachable()) return;
    try {
      const routes = await this.getRoutes();
      this.configurations.setRoutes(routes);
    } catch (error) {
      console.error(error);
    }
  }

  async createConfiguration(): Promise<void> {
    if (!isServerReachable()) return;
    try {
      const config = { params: { "match[app]": process.env.VUE_APP_BASE } };
      const configRes = await this.$axios.get(configuration.mainAPI, config);
      if (configRes.status === 200) {
        this.createConfigApp(configRes.data.configurationmobile);
        this.createConfiSettings(configRes.data.settings);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async createHotelConfig(): Promise<void> {
    if (!isServerReachable()) return;
    try {
      const params = { "match[app]": "hotel" };
      const res = await this.$axios.get(configuration.mainAPI, { params });
      if (res.status === 200) {
        this.createConfigHotel(res.data.hotel);
      }
    } catch (e) {
      console.error(e);
    }
  }

  async crealeCurrencyList(): Promise<void> {
    const uri = configuration.currency.main;
    const params = { "match[show]": true };
    try {
      const res = await this.$axios.get(uri, { params });
      if (res.status === 200) {
        this.useCurrencyStore.setCurrencies(res.data);
      }
    } catch (error) {
      console.error(error);
    }
  }

  async createInformelList(): Promise<void> {
    if (!isServerReachable()) return;
    console.log("createInformelList");

    const uri = service.mainAPI;
    try {
      const res: AxiosResponse<{ requests: HotelService[] }> = await this.$axios.get(uri);
      if (res.status === 200) {
        this.mapServicesToInformers(res.data.requests);
        console.log(res.data.requests);
      }
    } catch (error) {
      console.error(error);
    }
  }
}
